import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useTaxStatements from 'features/myVaro/hooks/statementsAndForms/useTaxStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';
import {
  showZendeskArticle,
  zendeskArticleIds,
} from 'shared/utils/zendesk/zendeskUtils';

const TaxStatementsScreen = () => {
  const router = useRouter();
  const flow = useVaroUserFlow();
  const { setTaxYearSelectionScreenTitle } = useContext(MyVaroContext);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms',
  });

  const { taxStatementsDetails, errorModel, loading } = useTaxStatements({});
  const onYearPress = (year: string) => {
    router.push({
      pathname: `/${flow}/statements-and-forms/tax-statements/[year]`,
      params: { year: year },
    });
  };

  const handleZendeskArticle = () => {
    showZendeskArticle(zendeskArticleIds.TAX_FORMS);
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('taxStatements.title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {taxStatementsDetails?.length ? (
        <FlatList
          data={taxStatementsDetails}
          renderItem={({ item }) => (
            <ListItem
              analyticsId={item?.id}
              key={item?.id}
              primaryCopy={item?.title}
              onPress={() => {
                setTaxYearSelectionScreenTitle?.(item?.title);
                onYearPress(item?.year?.toString() ?? '');
              }}
            />
          )}
          ItemSeparatorComponent={() => (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          )}
          keyExtractor={(item) => item?.id}
          removeClippedSubviews={false}
        />
      ) : (
        <View style={styles.emptyStateScreenView}>
          {!loading ? (
            <EmptyStateScreen
              headlineText={translatedText('taxStatements.title')}
              content={
                <BodyBaseText
                  style={styles.bodyTextContainer}
                  numberOfLines={0}
                >
                  {translatedText('taxStatements.textBeforeFAQ')}
                  <HyperlinkBaseText
                    onPress={handleZendeskArticle}
                    numberOfLines={0}
                  >
                    {translatedText('taxStatements.faqText')}
                  </HyperlinkBaseText>
                  {translatedText('taxStatements.textAfterFAQ')}
                </BodyBaseText>
              }
              illustrationKey="CALCULATOR_B"
            />
          ) : null}
        </View>
      )}

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyTextContainer: { textAlign: 'center' },
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  emptyStateScreenView: { flexGrow: 1, paddingHorizontal: theme.spacing.m },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default TaxStatementsScreen;
